import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { UNIT_ROLES, UNIT_ROLES_LABELS, UNIT_ROLES_DESCRIPTIONS } from '@/constants/unit-roles';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowSquareOut as ArrowSquareOutIcon,
  House as HouseIcon,
  MapPin as MapPinIcon,
  PlusCircle as PlusCircleIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import type { Role } from '@/types/role';
import type { Unit } from '@/types/v2/unit';
import axios from '@/lib/v2/axios';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { useUserTitles } from '@/hooks/use-titles';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';

import { AssignUnitDialog } from './assign-unit-dialog';

// ─── Unit card ────────────────────────────────────────────────────────────────

function UnitCard({ unit, residentId }: { unit: Unit; residentId: string }): React.JSX.Element {
  const { community } = useUser();
  const queryClient = useQueryClient();
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = React.useState(false);

  const handleRoleUpdate = async (roleName: string) => {
    try {
      const response = await axios.patch(`/api/v2/communities/${community?.id}/residents/${residentId}/unit-role`, {
        unit_id: String(unit.uuid ?? unit.id),
        role_name: roleName,
      });
      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ['residents', community?.id, residentId] });
        toast.success('Unit role updated');
      }
    } catch {
      toast.error('Failed to update unit role');
    }
  };

  const handleRemoveUnit = async () => {
    try {
      setIsRemoving(true);
      const response = await axios.delete(`/api/v1/users/${residentId}/units/${unit.id}`);
      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ['residents', community?.id, residentId] });
        queryClient.invalidateQueries({ queryKey: ['residents', community?.id] });
        toast.success('Removed from unit');
        setShowRemoveDialog(false);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to remove user from unit');
    } finally {
      setIsRemoving(false);
    }
  };

  const currentRoleName = unit.role?.name || 'unit_resident';
  const unitLabel = unit.unit_number ? `Unit ${unit.unit_number}` : unit.address || 'Unit';

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
      {/* Unit header bar */}
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <HouseIcon size={14} style={{ opacity: 0.5 }} />
          <Link
            href={paths.units.details(unit.uuid ?? String(unit.id))}
            sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {unitLabel}
          </Link>
        </Stack>
        <Link
          href={paths.units.details(unit.uuid ?? String(unit.id))}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: 'text.secondary', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 500, '&:hover': { color: 'primary.main' } }}
        >
          View
          <ArrowSquareOutIcon size={11} />
        </Link>
      </Stack>

      {/* Unit body */}
      <Box sx={{ px: 2, py: 2 }}>
        <Grid container spacing={2}>
          {/* Address */}
          {(unit.address || unit.city) && (
            <Grid item xs={12} sm={6}>
              <Typography color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
                Address
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start', color: 'text.primary' }}>
                <MapPinIcon size={13} style={{ marginTop: 2, flexShrink: 0, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                  {[unit.address, unit.city, unit.state, unit.zip_code].filter(Boolean).join(', ')}
                </Typography>
              </Stack>
            </Grid>
          )}

          {/* Unit Role */}
          <Grid item xs={12} sm={6}>
            <Typography color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
              Unit Role
            </Typography>
            <RoleGuard
              requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
              requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
            >
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  onChange={(e) => handleRoleUpdate(e.target.value)}
                  sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                  value={currentRoleName || ''}
                >
                  {Object.entries(UNIT_ROLES).map(([, value]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <Typography sx={{ fontWeight: 600, color: value === 'unit_owner' ? 'primary.main' : 'text.primary' }} variant="body2">
                          {UNIT_ROLES_LABELS[value]}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          {UNIT_ROLES_DESCRIPTIONS[value]}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </RoleGuard>
          </Grid>
        </Grid>

        {/* Remove button */}
        <RoleGuard
          requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
          requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
        >
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              color="error"
              disabled={isRemoving}
              onClick={() => setShowRemoveDialog(true)}
              size="small"
              startIcon={<TrashIcon size={14} />}
              sx={{ textTransform: 'none' }}
              variant="text"
            >
              Remove from Unit
            </Button>
          </Box>
        </RoleGuard>
      </Box>

      <Dialog fullWidth maxWidth="sm" onClose={() => setShowRemoveDialog(false)} open={showRemoveDialog}>
        <DialogTitle sx={{ pb: 1 }}>Remove User from Unit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this user from <strong>{unitLabel}</strong>?
            This action cannot be undone and will remove all unit-related roles and permissions.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button disabled={isRemoving} onClick={() => setShowRemoveDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={isRemoving}
            onClick={handleRemoveUnit}
            startIcon={isRemoving ? undefined : <TrashIcon size={16} />}
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            {isRemoving ? 'Removing…' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Community Roles Section (left column) ────────────────────────────────────

interface CommunityRolesSectionProps {
  communityRoles: Role[];
  communityTitle?: string | number | null;
  loading?: boolean;
}

export function CommunityRolesSection({ communityRoles, communityTitle, loading = false }: CommunityRolesSectionProps): React.JSX.Element {
  const { community } = useUser();
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const { data: userTitles } = useUserTitles();

  const handleRoleUpdate = async (roleName: string) => {
    if (!community) return;
    setSelectedRoleName(roleName);
    setIsSavingRole(true);
    try {
      const response = await axios.post(`/api/v2/communities/${community.id}/residents/${userId}/assign-role`, { role: roleName });
      if (response.status !== 200) throw new Error('Failed to update role');
      await queryClient.invalidateQueries({ queryKey: ['residents', community.id, userId] });
      toast.success('Community role updated');
    } catch {
      setSelectedRoleName(currentRoleName);
      toast.error('Failed to update community role');
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleTitleUpdate = async (titleId: string | null) => {
    try {
      setIsSavingTitle(true);
      if (!community) return;
      const response = await axios.patch(`/api/v2/communities/${community.id}/residents/${userId}/community-title`, {
        title_id: titleId ? Number(titleId) : null,
      });
      if (response.status !== 200) throw new Error('Failed to update title');
      queryClient.invalidateQueries({ queryKey: ['residents', community.id, userId] });
      toast.success('Community title updated');
    } catch {
      setSelectedTitleId(currentTitleId);
      toast.error('Failed to update community title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const currentRoleName = communityRoles.length > 0 ? communityRoles[0].name : 'community_member';

  const [selectedRoleName, setSelectedRoleName] = React.useState<string>(currentRoleName);
  const [isSavingRole, setIsSavingRole] = React.useState(false);
  React.useEffect(() => {
    if (!isSavingRole) setSelectedRoleName(currentRoleName);
  }, [currentRoleName, isSavingRole]);

  const currentTitleId = React.useMemo(() => {
    if (!communityTitle || !userTitles) return '';
    if (typeof communityTitle === 'number' || !isNaN(Number(communityTitle))) return String(communityTitle);
    const match = userTitles.find((t: any) => t.title === communityTitle);
    return match ? String(match.id) : '';
  }, [communityTitle, userTitles]);

  const [selectedTitleId, setSelectedTitleId] = React.useState<string>(currentTitleId);
  const [isSavingTitle, setIsSavingTitle] = React.useState(false);
  React.useEffect(() => {
    if (!isSavingTitle) setSelectedTitleId(currentTitleId);
  }, [currentTitleId, isSavingTitle]);

  const sortedUserTitles = React.useMemo(() => {
    if (!Array.isArray(userTitles)) return [];
    return [...userTitles]
      .filter((t: any) => t.title?.toLowerCase() !== 'board member')
      .sort((a: any, b: any) => Number(a.id) - Number(b.id));
  }, [userTitles]);

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} width="30%" variant="text" />
        <Skeleton height={40} variant="rectangular" sx={{ borderRadius: 1 }} />
        <Skeleton height={40} variant="rectangular" sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  const isBoardMember = selectedRoleName === 'board_member';

  return (
    <Box>
      <SectionLabel icon={<HouseIcon size={14} />}>Community &amp; Roles</SectionLabel>
      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={isBoardMember ? 12 : 6}>
          <Typography color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
            Community Role
          </Typography>
          <RoleGuard
            requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                disabled={isSavingRole}
                onChange={(e) => handleRoleUpdate(e.target.value)}
                sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                value={selectedRoleName || ''}
              >
                <MenuItem value="community_manager">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, color: 'primary.main' }} variant="body2">Community Manager</Typography>
                    <Typography color="text.secondary" variant="caption">Can manage community settings and residents</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="board_member">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }} variant="body2">Board Member</Typography>
                    <Typography color="text.secondary" variant="caption">Elevated visibility into community requests</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="community_member">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }} variant="body2">Community Member</Typography>
                    <Typography color="text.secondary" variant="caption">Regular community member</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </RoleGuard>
        </Grid>

        {!isBoardMember && (
          <Grid item xs={12} sm={6}>
            <Typography color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
              Community Title
            </Typography>
            <RoleGuard
              requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
              requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
            >
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  onChange={(e) => {
                    const v = (e.target.value) || '';
                    setSelectedTitleId(v);
                    void handleTitleUpdate(v || null);
                  }}
                  sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                  value={selectedTitleId}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography sx={{ fontWeight: 600, color: 'text.secondary' }} variant="body2">No Title</Typography>
                      <Typography color="text.secondary" variant="caption">No specific title assigned</Typography>
                    </Box>
                  </MenuItem>
                  {sortedUserTitles.map((title: any) => (
                    <MenuItem key={title.id} value={String(title.id)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <Typography sx={{ fontWeight: 600 }} variant="body2">{title.title}</Typography>
                        <Typography color="text.secondary" variant="caption">{title.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </RoleGuard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ─── Unit Details Section (left column) ───────────────────────────────────────

interface UnitDetailsSectionProps {
  units: Unit[];
  loading?: boolean;
}

export function UnitDetailsSection({ units, loading = false }: UnitDetailsSectionProps): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const [assignUnitDialogOpen, setAssignUnitDialogOpen] = React.useState(false);

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} width="20%" variant="text" />
        <Skeleton height={100} variant="rectangular" sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  return (
    <Box>
      <SectionLabel
        icon={<HouseIcon size={14} />}
        action={
          <RoleGuard
            requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <Button
              size="small"
              startIcon={<PlusCircleIcon size={14} />}
              onClick={() => setAssignUnitDialogOpen(true)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Add to Unit
            </Button>
          </RoleGuard>
        }
      >
        Units
        {units.length > 0 && (
          <Chip label={units.length} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} variant="outlined" />
        )}
      </SectionLabel>

      <Box sx={{ mt: 1.5 }}>
        {units.length > 0 ? (
          <Stack spacing={1.5}>
            {units.map((unit) => (
              <UnitCard key={unit.id} residentId={userId!} unit={unit} />
            ))}
          </Stack>
        ) : (
          <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
            No units assigned.
          </Typography>
        )}
      </Box>

      <AssignUnitDialog
        onClose={() => setAssignUnitDialogOpen(false)}
        onSuccess={() => setAssignUnitDialogOpen(false)}
        open={assignUnitDialogOpen}
        userId={userId!}
      />
    </Box>
  );
}
